import { useState } from "react";
import { useViewer } from "../../../state/ViewerProvider";
import { useUi } from "../../../state/editor/useEditor";
import { pickFile } from "../../../util/download";
import { Icon } from "../../kit";
import { MiniBtn, TextActionInput } from "../inspector/rows";
import { LibraryGrid, type LibItem } from "../library/LibraryGrid";
import { useManifest } from "../library/useManifest";

interface HdriEntry { id: string; label: string; file: string; thumb: string }

/**
 * Environment panel — a clean list of the built-in HDR presets (each
 * `setEnvironment(id)`) with an active highlight, plus a URL loader
 * (`loadEnvFromUrl`) and a file upload (`loadEnvFromFile`). The full thumbnail
 * grid + Poly Haven library land in Phase 2.
 *
 * Every control here is a multi-second load (1–2 MB HDR fetch + decode + PMREM
 * prefilter), so each one holds a pending state until its RPC resolves, the
 * check-mark moves only on success, and a failure is shown inline.
 *
 * The applied id lives in the persisted `ui.lastApplied` map rather than in
 * component state: LeftPanel unmounts the panel body when it is collapsed, so
 * local state would drop the highlight on every close/reopen, and
 * `lastApplied.environment` is the same key the Scene tab's "Environment HDR"
 * picker reads — writing it keeps the two surfaces agreeing.
 */
export function EnvironmentPanel() {
  const { client, catalogs } = useViewer();
  const r = client.ring;
  const [ui, patchUi] = useUi();
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);
  const [customBusy, setCustomBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { items: hdris, urlFor, thumbFor } = useManifest<HdriEntry>("/env/manifest.json", "env");

  const activePreset = ui.lastApplied.environment || null;
  const activeHdri = ui.lastApplied.envHdri || null;
  /** Presets and library HDRIs are mutually exclusive; "" means "not this one". */
  const setApplied = (preset: string, hdri: string) =>
    patchUi({ lastApplied: { ...ui.lastApplied, environment: preset, envHdri: hdri } });

  const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Failed to load environment.");

  const select = async (id: string) => {
    if (pendingPreset || customBusy) return;
    setError(null);
    setPendingPreset(id);
    try {
      await r.setEnvironment(id);
      setApplied(id, "");
    } catch (e) {
      setError(errText(e));
    } finally {
      setPendingPreset(null);
    }
  };

  const libItems: LibItem[] = hdris.map((h) => ({ id: h.id, label: h.label, thumb: thumbFor(h) }));
  // Returned (not `void`ed) so LibraryGrid can spin the tile and lock its
  // siblings for the whole PMREM bake instead of accepting five queued loads.
  // Address the HDR by its manifest entry, never by `${id}.hdr` — that only
  // held while ids happened to equal filenames, and an API-served preset has a
  // database id, so the guessed URL 404s.
  const selectHdri = (it: LibItem) => {
    const entry = hdris.find((h) => h.id === it.id);
    if (!entry) return;
    return r.loadEnvFromUrl(urlFor(entry)).then(() => setApplied("", it.id));
  };

  const uploadEnv = async () => {
    // pickFile only awaits `onFile` — it discards the callback's rejection — so
    // capture the decode failure and re-throw it once the dialog round-trip is
    // over, letting whichever control started the upload surface it.
    let failure: unknown;
    await pickFile(".hdr,.exr", async (f) => {
      try {
        await r.loadEnvFromFile(f);
        setApplied("", "");
      } catch (e) {
        failure = e;
      }
    });
    if (failure) throw failure;
  };

  const busy = pendingPreset !== null || customBusy;

  return (
    <div className="flex flex-col gap-4 p-3">
      <p className="text-[11px] leading-snug text-ed-text-3">
        Lights the scene and what the metals reflect. Loading a metal HDR (Lighting › Metal reflections map) pins the
        metal reflection to that HDR instead.
      </p>
      <div className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ed-text-2">Presets</span>
        <div className="flex flex-col gap-1">
          {catalogs.envPresets.map((p) => {
            const on = activePreset === p.id;
            const loading = pendingPreset === p.id;
            return (
              <button
                key={p.id}
                onClick={() => void select(p.id)}
                disabled={busy}
                aria-busy={loading || undefined}
                className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-left text-[13px] transition-colors ${
                  on
                    ? "border-ed-accent bg-ed-active text-ed-text"
                    : "border-ed-border bg-white text-ed-text hover:border-ed-accent"
                }${busy && !loading ? " pointer-events-none opacity-50" : ""}`}
              >
                <span className="truncate">{p.label}</span>
                {loading ? <Icon name="progress_activity" size={15} className="animate-spin" /> : on ? <Icon name="check" size={15} /> : null}
              </button>
            );
          })}
        </div>
        {error ? <span className="text-[11px] text-ed-red">{error}</span> : null}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-semibold text-ed-text-2">Custom HDR / EXR</span>
        <TextActionInput
          placeholder="HDR/EXR URL…"
          action="Load"
          // Returned so the input stays disabled + spinning for the real fetch,
          // and keeps the URL on failure instead of silently clearing it.
          onSubmit={(url) => {
            setError(null);
            return r.loadEnvFromUrl(url).then(() => setApplied("", ""));
          }}
        />
        <MiniBtn
          icon="upload"
          busy={customBusy}
          disabled={pendingPreset !== null}
          onClick={() => {
            setError(null);
            setCustomBusy(true);
            // pickFile settles when the decode finishes, not when the OS dialog
            // opens, so the spinner covers the actual load.
            uploadEnv()
              .catch((e) => setError(errText(e)))
              .finally(() => setCustomBusy(false));
          }}
        >
          Upload HDR/EXR
        </MiniBtn>
      </div>

      {libItems.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-[12px] font-semibold text-ed-text-2">Studio HDRI library</span>
          <LibraryGrid
            items={libItems}
            activeId={activeHdri}
            onSelect={selectHdri}
            onUpload={uploadEnv}
            uploadAccept=".hdr,.exr"
            view={ui.libraryView}
            disabled={busy}
          />
          <span className="text-[11px] text-ed-text-3">Poly Haven · CC0</span>
        </div>
      ) : null}
    </div>
  );
}
